// @flow

import React, { useState, useEffect } from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { Field, reduxForm } from "redux-form";
import type { FormProps } from "redux-form";
import { Dropdown } from "@components/form";
import { Form, FormGroup, Label, type StepProps, Button, ButtonGroup } from "@components";
import type { State } from "@store/reducers";
import { selectConfigureContactValues } from "@modules/hierarchy/selectors";
import { NotificationTypeExposure } from "@modules/shared/components";
import type { NotificationContact } from "../../reducer/hierarchy/types";

export type FilterOption = $Exact<{ value: string, label: string }>;

type PropsFromState = $Exact<{
  formData: Object,
}>;

type OwnProps = $Exact<{
  selectedNotificationContact: NotificationContact,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...StepProps,
  ...PropsFromLocalize,
  ...$Exact<FormProps>, // https://stackoverflow.com/a/44771268/220839
  ...OwnProps,
}>;

const notificationTypes = ["BASIC_NOTIFICATION", "STANDARD_NOTIFICATION", "FULL_NOTIFICATION"];

export function ConfigureContact(props: Props) {
  const selectedNotificationType = props.data?.notificationType;

  useEffect(() => {
    if (props.selectedNotificationContact) {
      const { isCascading, notificationType } = props.selectedNotificationContact || {};
      const stepData = {
        isCascading: isCascading ? isCascading.toString() : "false",
        notificationType: notificationType ? notificationType : "FULL_NOTIFICATION",
      };
      props.initialize && props.initialize(stepData);
    } else {
      const stepData = {
        isCascading: "false",
        notificationType: "BASIC_NOTIFICATION",
      };
      props.initialize && props.initialize(stepData);
    }
  }, [props.initialize, props.selectedNotificationContact]);

  function setNotificationType(notificationType: string) {
    return function() {
      props.change("notificationType", notificationType);
    };
  }

  return (
    props.editable && (
      <div className="w-100 d-flex justify-content-center mb-5">
        <Form className="col-sm-8">
          <FormGroup className="mb-0 mr-2 p-0 w-100">
            <Label>{props.t("NOTIFICATION_TYPE")}</Label>
            <ButtonGroup fillSpace className="mb-4">
              {notificationTypes.map(notification => {
                return (
                  <Button
                    key={notification}
                    type="button"
                    color={notification === props.formData.notificationType ? "selected" : null}
                    onClick={setNotificationType(notification)}
                  >
                    {props.t(notification)}
                  </Button>
                );
              })}
            </ButtonGroup>
          </FormGroup>
          <NotificationTypeExposure currentType={selectedNotificationType} />
          <FormGroup className="mb-0 mr-2 p-0 w-100">
            <Label for="isCascading">{props.t("DEPARTMENT_SCOPE")}</Label>
            <Field
              id="isCascading"
              name="isCascading"
              component={Dropdown}
              options={[
                {
                  label: props.t("THIS_DEPARTMENT_ONLY"),
                  value: "false",
                },
                {
                  label: props.t("THIS_DEPARTMENT_AND_ITS_CHILDREN"),
                  value: "true",
                },
              ]}
            />
          </FormGroup>
        </Form>
      </div>
    )
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  formData: selectConfigureContactValues(state),
});

export default reduxForm({
  form: "configure-contact",
})(localize()(connect(mapStateToProps, null)(ConfigureContact)));
